import { useEffect, useState } from 'react';
import { Box, CircularProgress, FormControl, TextField, Typography } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import type { FieldValidation } from '@rjsf/utils';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import { identityApiRef, configApiRef } from '@backstage/core-plugin-api';

const ALL_ENVIRONMENTS = [
  { key: 'des', label: 'Desarrollo' },
  { key: 'int', label: 'Integración' },
  { key: 'sta', label: 'Staging' },
  { key: 'pre', label: 'Preproducción' },
  { key: 'pro', label: 'Producción' },
];

const GKE_INFRA_PROJECT_REF = 'project:default/prj0035937';

export const ApplicationComponentEnvField = ({
  onChange,
  rawErrors,
  required,
  formData,
}: FieldExtensionComponentProps<any>) => {
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);
  const config = useApi(configApiRef);

  const [userApplicationNumbers, setUserApplicationNumbers] = useState<string[]>([]);

  const [applications, setApplications] = useState<any[]>([]);
  const [components, setComponents] = useState<any[]>([]);
  const [component, setComponent] = useState('');
  const [environment, setEnvironment] = useState('');

  const [cdConfigEnvs, setCdConfigEnvs] = useState<string[]>([]);
  const [envTemplates, setEnvTemplates] = useState<Record<string, string>>({});

  const [loadingApplications, setLoadingApplications] = useState(false);
  const [loadingComponents, setLoadingComponents] = useState(false);
  const [loadingEnvs, setLoadingEnvs] = useState(false);

  const [noComponentsWithCI, setNoComponentsWithCI] = useState(false);
  const [restrictEnvironmentsForGkeProject, setRestrictEnvironmentsForGkeProject] = useState(false);

  // Local states
  const [application, setApplication] = useState('');

  // Obtener projectCodes del usuario logueado (o impersonado)
  useEffect(() => {
    const fetchUserApplications = async () => {
      const identity = await identityApi.getBackstageIdentity();
      const userEntityRef = identity.userEntityRef;

      // Obtener email del usuario desde el catálogo
      let userEmail = userEntityRef.split('/')[1];
      try {
        const userEntity = await catalogApi.getEntityByRef(userEntityRef);
        const profile = userEntity?.spec?.profile as any;
        if (profile?.email) {
          userEmail = profile.email;
        }
      } catch (err) {
        console.warn('⚠️ Could not fetch user entity, using username instead:', err);
      }

      // Check for impersonated user (for development/testing)
      const impersonatedFromStorage = localStorage.getItem('backstage.impersonatedUser');
      const impersonatedFromConfig = config.getOptionalString('openfga.impersonatedUser');
      const impersonatedUser = impersonatedFromStorage || impersonatedFromConfig;

      let effectiveUser = impersonatedUser || userEmail;
      if (impersonatedUser && impersonatedUser !== userEmail) {
        try {
          const impersonatedUserRef = `user:default/${impersonatedUser}`;
          const impersonatedUserEntity = await catalogApi.getEntityByRef(impersonatedUserRef);
          const impersonatedProfile = impersonatedUserEntity?.spec?.profile as any;
          if (impersonatedProfile?.email) {
            effectiveUser = impersonatedProfile.email;
          }
        } catch (err) {
          console.warn('⚠️ Could not fetch impersonated user entity, using username instead:', err);
        }
        console.log('🎭 Impersonating user:', effectiveUser, '(from ApplicationProjectsField)');
      }

      const backendUrl = config.getString('backend.baseUrl');
      const token = (await identityApi.getCredentials()).token;

      const response = await fetch(
        `${backendUrl}/api/openfga-client/list-user-entities`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            user: `user:${effectiveUser}`,
            type: 'application', // 👈 CAMBIO CLAVE
          }),
        },
      );

      const data = await response.json();

      const appNumbers = (data.objects ?? [])
        .map((a: string) => {
          const match = a.match(/^application:(apm\d+)$/i);
          return match ? match[1].toLowerCase() : null;
        })
        .filter(Boolean) as string[];

      setUserApplicationNumbers(Array.from(new Set(appNumbers)));
    };

    fetchUserApplications();
  }, [identityApi, catalogApi, config]);

  // -----------------------------------------------------------------------------------
  // ✔️ 1. Sincronizar con formData cuando cambia (ESTO ERA EL PROBLEMA)
  // -----------------------------------------------------------------------------------
  useEffect(() => {
    if (!formData) return;

    setApplication(formData.application || '');
    setComponent(formData.component || '');
    setEnvironment(formData.environment || '');
  }, [formData]);

  // -----------------------------------------------------------------------------------
  // ✔️ 2. Cargar Aplicaciones del usuario
  // -----------------------------------------------------------------------------------
  useEffect(() => {
    const fetchApplications = async () => {
      if (userApplicationNumbers.length === 0) {
        setApplications([]);
        return;
      }

      setLoadingApplications(true);

      try {
        const resp = await catalogApi.getEntities({
          filter: { kind: 'Application' },
        });

        const filtered = resp.items.filter(app =>
          userApplicationNumbers.includes(
            String(app.spec?.number ?? '').toLowerCase(),
          ),
        );

        setApplications(filtered);
      } finally {
        setLoadingApplications(false);
      }
    };

    fetchApplications();
  }, [userApplicationNumbers, catalogApi]);

  // -----------------------------------------------------------------------------------
  // ✔️ 4. Handle de selección de aplicación
  // -----------------------------------------------------------------------------------
  const handleApplicationChange = async (app: any) => {
    const ref = `application:${app.metadata.namespace}/${app.metadata.name}`;

    setApplication(ref);
    setComponent('');
    setEnvironment('');

    // ✅ Construir dinámicamente el objeto
    const payload: any = {
      application: ref,
      component: '',
      environment: '',
    };

    onChange(payload);
  };

  const handleComponentChange = async (comp: any) => {
    const ref = `component:${comp.metadata.namespace}/${comp.metadata.name}`;
    const repoName = comp.metadata.title;

    setComponent(ref);
    setEnvironment('');
    setLoadingEnvs(true);

    const token = (await identityApi.getCredentials()).token;

    const params = new URLSearchParams({
      owner: 'ITACCIONA',
      repoName: 'ats_APM0003211__devops_configuration',
      artifactName: repoName,
    });

    const url = `${config.getString('backend.baseUrl')}/api/devops-api-proxy/github-ci-cd-config-envs?${params.toString()}`;

    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });


      if (!response.ok) {
        setCdConfigEnvs([]);
      } else {
        const data = await response.json();

        setCdConfigEnvs(
          Array.isArray(data.cd_config)
            ? data.cd_config.filter((env: string) => env !== 'base')
            : [],
        );

        setEnvTemplates(data.templates ?? {});
      }

      setEnvironment('');

      onChange({
        application: application,
        component: ref,
        environment: '',
      });

    } finally {
      setLoadingEnvs(false);
    }
  };

  const handleEnvironmentChange = (env: any) => {
    setEnvironment(env.key);

    onChange({
      application,
      component,
      environment: env.key,
    });
  };

  useEffect(() => {
    const fetchComponents = async () => {
      if (!application) {
        setComponents([]);
        setNoComponentsWithCI(false);
        return;
      }

      setLoadingComponents(true);
      setNoComponentsWithCI(false);

      try {
        const resp = await catalogApi.getEntities({
          filter: {
            kind: 'Component',
            'spec.system': application,
          },
        });

        const token = (await identityApi.getCredentials()).token;
        const backendUrl = config.getString('backend.baseUrl');

        const results = await Promise.all(
          resp.items.map(async (comp: any) => {
            try {
              const params = new URLSearchParams({
                owner: 'ITACCIONA',
                repoName: 'ats_APM0003211__devops_configuration',
                artifactName: comp.metadata.title,
              });

              const url = `${backendUrl}/api/devops-api-proxy/github-ci-cd-config-envs?${params.toString()}`;

              const res = await fetch(url, {
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
              });

              if (!res.ok) return null;

              const data = await res.json();

              const hasCI =
                Array.isArray(data.ci_config) && data.ci_config.length > 0;

              return hasCI ? comp : null;
            } catch {
              return null;
            }
          }),
        );

        const componentsWithCI = results.filter(Boolean);

        setComponents(componentsWithCI);

        // 🔥 AQUÍ LA CLAVE
        if (resp.items.length === 0) {
          // ❌ No hay repos en la aplicación
          setNoComponentsWithCI(true);
        } else if (componentsWithCI.length === 0) {
          // ⚠️ Hay repos pero ninguno tiene CI
          setNoComponentsWithCI(true);
        }

      } finally {
        setLoadingComponents(false);
      }
    };

    fetchComponents();
  }, [application, catalogApi, identityApi, config]);

  useEffect(() => {
    const hydrateEnvironments = async () => {
      if (!component) return;

      setLoadingEnvs(true);

      try {
        const comp = components.find(
          c => `component:${c.metadata.namespace}/${c.metadata.name}` === component,
        );

        if (!comp) return;

        const repoName = comp.metadata.title;

        const token = (await identityApi.getCredentials()).token;

        const params = new URLSearchParams({
          owner: 'ITACCIONA',
          repoName: 'ats_APM0003211__devops_configuration',
          artifactName: repoName,
        });

        const url = `${config.getString('backend.baseUrl')}/api/devops-api-proxy/github-ci-cd-config-envs?${params.toString()}`;

        const response = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          setCdConfigEnvs([]);
          setEnvTemplates({});
          return;
        }

        const data = await response.json();

        setCdConfigEnvs(
          Array.isArray(data.cd_config)
            ? data.cd_config.filter((env: string) => env !== 'base')
            : [],
        );

        setEnvTemplates(data.templates ?? {});
      } finally {
        setLoadingEnvs(false);
      }
    };

    hydrateEnvironments();
  }, [component, components, identityApi, config]);

  useEffect(() => {
    const resolveEnvironmentRestriction = async () => {
      if (!component) {
        setRestrictEnvironmentsForGkeProject(false);
        return;
      }

      const selectedComponent = components.find(
        c => `component:${c.metadata.namespace}/${c.metadata.name}` === component,
      );
      const componentSystem = selectedComponent?.spec?.system as string | undefined;

      if (!componentSystem) {
        setRestrictEnvironmentsForGkeProject(false);
        return;
      }

      try {
        const projectEntity = await catalogApi.getEntityByRef(GKE_INFRA_PROJECT_REF);
        const apps: string[] = (projectEntity?.spec as any)?.applications ?? [];
        setRestrictEnvironmentsForGkeProject(apps.includes(componentSystem));
      } catch {
        setRestrictEnvironmentsForGkeProject(false);
      }
    };

    resolveEnvironmentRestriction();
  }, [component, components, catalogApi]);

  const allowedEnvironments = restrictEnvironmentsForGkeProject
    ? ALL_ENVIRONMENTS.filter(env => env.key === 'des' || env.key === 'pro')
    : ALL_ENVIRONMENTS;

  const availableEnvironments = allowedEnvironments.filter(
    env => !cdConfigEnvs.includes(env.key),
  );

  const isRepoDisabled = !application || components.length === 0;
  const isEnvDisabled = !component || availableEnvironments.length === 0;

  const configuredEnvironments = allowedEnvironments.filter(env =>
    cdConfigEnvs.includes(env.key),
  );

  const notConfiguredEnvironments = allowedEnvironments.filter(
    env => !cdConfigEnvs.includes(env.key),
  );

  // -----------------------------------------------------------------------------------
  // ✔️ Render
  // -----------------------------------------------------------------------------------
  return (
    <FormControl margin="normal" required={required} fullWidth>
      {/* Application */}
      <Autocomplete
        options={applications}
        getOptionLabel={opt =>
          `${opt.metadata.title} (${opt.spec?.number})`
        }
        value={
          applications.find(
            a => `application:${a.metadata.namespace}/${a.metadata.name}` === application,
          ) || null
        }
        onChange={(_, newValue) => {
          if (newValue) handleApplicationChange(newValue);
        }}
        renderInput={params => (
          <TextField
            {...params}
            label="Application"
            required
            helperText={
              loadingApplications
                ? 'Loading applications...'
                : rawErrors?.[0] || 'Search for an Application'
            }
          />
        )}
      />

      {loadingComponents ? <CircularProgress size={30} /> : <Autocomplete
        options={components}
        disabled={isRepoDisabled}
        getOptionLabel={opt => opt.metadata.title || opt.metadata.name}
        value={
          components.find(
            c => `component:${c.metadata.namespace}/${c.metadata.name}` === component,
          ) || null
        }
        onChange={(_, newValue) => {
          if (newValue) handleComponentChange(newValue);
        }}
        renderInput={params => (
          <TextField
            {...params}
            label="Repository"
            required
            helperText={
              loadingComponents
                ? 'Checking repositories for CI...'
                : 'Only repositories with CI'
            }
          />
        )}
      />}
      {noComponentsWithCI && (
        <Typography variant="body2" color="error" style={{ marginTop: 8 }}>
          ⚠️ This application has repositories, but none with CI configured
        </Typography>
      )}

      {loadingComponents ? null : loadingEnvs ? <CircularProgress size={30} /> : <Autocomplete
        options={availableEnvironments}
        disabled={isEnvDisabled}
        getOptionLabel={opt => opt.label}
        value={availableEnvironments.find(e => e.key === environment) || null}
        onChange={(_, newValue) => {
          if (newValue) handleEnvironmentChange(newValue);
        }}
        renderInput={params => (
          <TextField
            {...params}
            label="Environment"
            required
            helperText={
              loadingEnvs
                ? 'Loading environments...'
                : rawErrors?.[0] || 'Only environments not yet configured'
            }
          />
        )}
      />}

      {restrictEnvironmentsForGkeProject && (
        <Typography variant="body2" style={{ marginTop: 8 }}>
          Componente perteneciente a Digital Hub, por lo tanto, mostrando solo los entornos de Desarrollo y Produccion.
        </Typography>
      )}

      {component && !loadingEnvs && (
        <Box
          mt={2}
          p={2}
          style={{
            borderRadius: '4px',
            border: '1px solid #3e404bff',
          }}
        >
          <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
            🌍 Environment Configuration
          </Typography>

          {/* ✅ CONFIGURADOS */}
          <Typography variant="body2" style={{ marginTop: 8 }}>
            <strong>Configured:</strong>
          </Typography>

          {configuredEnvironments.length === 0 ? (
            <Typography variant="body2">None</Typography>
          ) : (
            configuredEnvironments.map(env => (
              <Typography key={env.key} variant="body2">
                ✅ {env.label}
                {envTemplates[env.key] && (
                  <> — Template: <strong>{envTemplates[env.key]}</strong></>
                )}
              </Typography>
            ))
          )}

          {/* ❌ NO CONFIGURADOS */}
          <Typography variant="body2" style={{ marginTop: 8 }}>
            <strong>Available to configure:</strong>
          </Typography>

          {notConfiguredEnvironments.length === 0 ? (
            <Typography variant="body2">All environments configured</Typography>
          ) : (
            notConfiguredEnvironments.map(env => (
              <Typography key={env.key} variant="body2">
                ⬜ {env.label}
              </Typography>
            ))
          )}
        </Box>
      )}
    </FormControl>
  );
};

// -----------------------------------------------------------------------------------
// ✔️ Validación
// -----------------------------------------------------------------------------------
export const applicationComponentEnvCaseValidation = (
  value: { application: string, component: string, environment: string },
  validation: FieldValidation,
) => {
  if (!value.application) validation.addError('Application is required.');
  if (!value.component) validation.addError('Component is required.');
  if (!value.environment) validation.addError('Environment is required.');
};

// -----------------------------------------------------------------------------------
// ✔️ Registro del Custom Field
// -----------------------------------------------------------------------------------
export const ApplicationComponentEnvCaseFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'ApplicationComponentEnvField',
    component: ApplicationComponentEnvField,
    validation: applicationComponentEnvCaseValidation,
  }),
);
